import { useState, useEffect, useCallback } from 'react';
import {
  GalleryImage,
  BrandId,
  BRANDS,
  STORAGE_KEYS,
  DEFAULT_IMAGES,
} from '../lib/storage';

export function useGallery() {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [isAdmin, setIsAdminState] = useState(false);
  const [activeBrand, setActiveBrand] = useState<BrandId>('used');
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [error, setError] = useState<string | null>(null);

  // Helper function to get images for a brand - defined inside component to capture `images` state
  const getImagesByBrand = (brand: BrandId): GalleryImage[] => {
    const allImages = images.length > 0 ? images : loadLocalImages();
    return allImages.filter((img) => img.brandId === brand).sort((a, b) => a.sortOrder - b.sortOrder);
  };

  // Load images from images.json on mount
  useEffect(() => {
    loadImagesFromServer();
    // Admin mode should not persist across sessions - always start closed
    setIsAdminState(false);
    setAdminState(false);
  }, []);

  const loadImagesFromServer = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch('/images.json', {
        cache: 'no-cache', // Always fetch fresh
      });
      if (response.ok) {
        const serverImages = await response.json();
        if (Array.isArray(serverImages) && serverImages.length > 0) {
          // Merge with localStorage (local edits take precedence)
          const localImages = loadLocalImages();
          const merged = mergeImages(serverImages, localImages);
          setImages(merged);
          saveLocalImages(merged); // Cache merged result
          setIsLoading(false);
          return;
        }
      }
      // Fallback to localStorage
      const local = loadLocalImages();
      setImages(local.length > 0 ? local : DEFAULT_IMAGES);
    } catch (e) {
      console.warn('Failed to fetch images.json, using localStorage:', e);
      const local = loadLocalImages();
      setImages(local.length > 0 ? local : DEFAULT_IMAGES);
      setError('Using cached images - server unavailable');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshImages = useCallback(() => {
    loadImagesFromServer();
  }, [loadImagesFromServer]);

  const toggleAdmin = useCallback(() => {
    const newState = !isAdmin;
    setIsAdminState(newState);
    setAdminState(newState);
    if (newState) {
      // Redirect to PHP admin panel
      window.location.href = '/admin.php';
    }
  }, [isAdmin]);

  // Admin functions (for React admin panel - kept for compatibility)
  const handleAddImage = useCallback(async (
    source: File | string,
    brandId: BrandId,
    alt: string,
    isFeatured: boolean
  ): Promise<GalleryImage> => {
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      const processUrl = (url: string) => {
        const newImage = addImage({
          url,
          brandId,
          alt,
          isFeatured,
          sortOrder: Date.now(),
          rotation: 0,
        });
        setImages(loadLocalImages());
        setIsLoading(false);
        resolve(newImage);
      };

      if (typeof source === 'string') {
        processUrl(source);
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          processUrl(e.target?.result as string);
        };
        reader.onerror = () => {
          setIsLoading(false);
          reject(new Error('Failed to read file'));
        };
        reader.readAsDataURL(source);
      }
    });
  }, []);

  const handleUpdateImage = useCallback((updatedImage: GalleryImage) => {
    updateImage(updatedImage);
    setImages(loadLocalImages());
    if (selectedImage?.id === updatedImage.id) {
      setSelectedImage(updatedImage);
    }
  }, [selectedImage]);

  const handleDeleteImage = useCallback((id: string) => {
    deleteImage(id);
    setImages(loadLocalImages());
    if (selectedImage?.id === id) {
      setSelectedImage(null);
    }
  }, [selectedImage]);

  const handleSelectImage = useCallback((image: GalleryImage | null) => {
    setSelectedImage(image);
  }, []);

  const handleReorder = useCallback((brandId: BrandId, orderedIds: string[]) => {
    reorderImages(brandId, orderedIds);
    setImages(loadLocalImages());
  }, []);

  const handleEditImage = useCallback((image: GalleryImage) => {
    setSelectedImage(image);
  }, []);

  const handleNavigate = useCallback((direction: 'prev' | 'next') => {
    if (!selectedImage) return;
    const brandImages = getImagesByBrand(activeBrand);
    const currentIndex = brandImages.findIndex(img => img.id === selectedImage.id);
    if (direction === 'prev' && currentIndex > 0) {
      setSelectedImage(brandImages[currentIndex - 1]);
    } else if (direction === 'next' && currentIndex < brandImages.length - 1) {
      setSelectedImage(brandImages[currentIndex + 1]);
    }
  }, [selectedImage, activeBrand]);

  const brandImages = getImagesByBrand(activeBrand);
  const brand = BRANDS.find(b => b.id === activeBrand);

  return {
    images,
    isAdmin,
    activeBrand,
    setActiveBrand,
    selectedImage,
    setSelectedImage,
    isLoading,
    viewMode,
    setViewMode,
    toggleAdmin,
    handleAddImage,
    handleUpdateImage,
    handleDeleteImage,
    handleSelectImage,
    handleReorder,
    handleEditImage,
    handleNavigate,
    brandImages,
    brand,
    refreshImages,
    error,
  };
}

// LocalStorage helpers (kept for fallback/cache)
function loadLocalImages(): GalleryImage[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.IMAGES);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn('Failed to load images from storage:', e);
  }
  return [];
}

function saveLocalImages(images: GalleryImage[]): void {
  try {
    localStorage.setItem(STORAGE_KEYS.IMAGES, JSON.stringify(images));
  } catch (e) {
    console.error('Failed to save images:', e);
  }
}

function addImage(image: Omit<GalleryImage, 'id' | 'createdAt' | 'updatedAt'>): GalleryImage {
  const images = loadLocalImages();
  const newImage: GalleryImage = {
    ...image,
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  const updated = [...images, newImage].sort((a, b) => a.sortOrder - b.sortOrder);
  saveLocalImages(updated);
  return newImage;
}

function updateImage(updatedImage: GalleryImage): void {
  const images = loadLocalImages();
  const updated = images.map((img) =>
    img.id === updatedImage.id ? { ...updatedImage, updatedAt: new Date().toISOString() } : img
  );
  saveLocalImages(updated);
}

function deleteImage(id: string): void {
  const images = loadLocalImages();
  const filtered = images.filter((img) => img.id !== id);
  saveLocalImages(filtered);
}

function reorderImages(brandId: BrandId, orderedIds: string[]): void {
  const images = loadLocalImages();
  const brandImages = images.filter((img) => img.brandId === brandId);
  const otherImages = images.filter((img) => img.brandId !== brandId);

  const reordered = orderedIds.map((id, index) => {
    const img = brandImages.find((b) => b.id === id);
    return img ? { ...img, sortOrder: index + 1, updatedAt: new Date().toISOString() } : null;
  }).filter(Boolean) as GalleryImage[];

  saveLocalImages([...reordered, ...otherImages].sort((a, b) => a.sortOrder - b.sortOrder));
}

// Merge server images with local edits (local takes precedence)
function mergeImages(serverImages: GalleryImage[], localImages: GalleryImage[]): GalleryImage[] {
  const localMap = new Map(localImages.map(img => [img.id, img]));
  const serverMap = new Map(serverImages.map(img => [img.id, img]));
  
  // Start with server images
  const merged = [...serverImages];
  
  // Apply local updates/deletes
  for (const local of localImages) {
    const existingIndex = merged.findIndex(img => img.id === local.id);
    if (existingIndex >= 0) {
      // Update existing
      merged[existingIndex] = local;
    } else if (!serverMap.has(local.id)) {
      // New local image not on server yet
      merged.push(local);
    }
  }
  
  // Remove images that were deleted locally but exist on server
  const final = merged.filter(img => localMap.has(img.id) || !localImages.some(l => l.id === img.id));
  
  // Sort by brand order then sortOrder
  const brandOrder = BRANDS.map(b => b.id);
  return final.sort((a, b) => {
    const aBrand = brandOrder.indexOf(a.brandId);
    const bBrand = brandOrder.indexOf(b.brandId);
    if (aBrand !== bBrand) return aBrand - bBrand;
    return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  });
}

function setAdminState(isAdmin: boolean): void {
  localStorage.setItem(STORAGE_KEYS.ADMIN_STATE, JSON.stringify({ isAdmin }));
}